import "@trimblemaps/trimblemaps-js/trimblemaps.css";

import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import { useEffect, useRef } from "react";

import { environmentConfig } from "~/shared/utils/environment-config.ts";

import {
	applyDotScale,
	createDotElement,
	createLabeledMarkerElement,
	getMarkerColor,
} from "./marker-utils.ts";
import styles from "./tracking-map.module.css";

export type MapPingType = "tracking" | "pickup" | "delivery" | "stop";

export type MapPing = {
	readonly id: string;
	readonly latitude: number;
	readonly longitude: number;
	readonly type?: MapPingType;
	readonly label?: string;
	readonly markerLabel?: string;
	readonly timestamp?: string;
};

type TrackingMapProps = {
	readonly pings: readonly MapPing[];
	readonly height?: string;
	readonly onPingClick?: (ping: MapPing) => void;
};

const DEFAULT_CENTER: [number, number] = [-84.5, 39.1]; // [lng, lat] Cincinnati
const DEFAULT_ZOOM = 4;

export function TrackingMap({
	pings,
	height = "400px",
	onPingClick,
}: TrackingMapProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<TrimbleMaps.Map | null>(null);
	const markersRef = useRef<TrimbleMaps.Marker[]>([]);
	const dotMarkersRef = useRef<
		{ element: HTMLDivElement; color: string; latitude: number }[]
	>([]);

	useEffect(() => {
		if (!containerRef.current || mapRef.current) return;

		TrimbleMaps.setAPIKey(environmentConfig.trimbleMapsApiKey);

		const center: [number, number] = pings[0]
			? [pings[0].longitude, pings[0].latitude]
			: DEFAULT_CENTER;

		const map = new TrimbleMaps.Map({
			container: containerRef.current,
			center,
			zoom: DEFAULT_ZOOM,
			attributionControl: false,
		});

		mapRef.current = map;

		return () => {
			try {
				map.remove();
			} catch {
				// TrimbleMaps aborts in-flight tile/style requests when the map is
				// removed. If teardown happens before the map finishes loading (e.g.
				// React StrictMode's double-mount or a fast unmount), that abort can
				// surface as an AbortError. It's harmless during cleanup, so ignore it.
			}
			mapRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only mount once
	}, []);

	useEffect(() => {
		const map = mapRef.current;
		if (!map) return;

		const addMarkers = () => {
			for (const marker of markersRef.current) {
				marker.remove();
			}
			markersRef.current = [];
			dotMarkersRef.current = [];

			for (const ping of pings) {
				const color = getMarkerColor(ping.type ?? "tracking");
				const isTracking = (ping.type ?? "tracking") === "tracking";

				let markerOptions: TrimbleMaps.MarkerOptions;
				if (isTracking) {
					const el = createDotElement(color);
					applyDotScale(el, map.getZoom(), ping.latitude, color);
					dotMarkersRef.current.push({
						element: el,
						color,
						latitude: ping.latitude,
					});
					markerOptions = { element: el };
				} else if (ping.markerLabel) {
					markerOptions = {
						element: createLabeledMarkerElement(color, ping.markerLabel),
					};
				} else {
					markerOptions = { color };
				}

				const marker = new TrimbleMaps.Marker(markerOptions).setLngLat([
					ping.longitude,
					ping.latitude,
				]);

				if (ping.label) {
					const popup = new TrimbleMaps.Popup({ offset: 25 }).setText(
						ping.label,
					);
					marker.setPopup(popup);
				}

				if (onPingClick) {
					marker.getElement().addEventListener("click", () => {
						onPingClick(ping);
					});
				}

				marker.addTo(map);
				markersRef.current.push(marker);
			}
		};

		if (map.loaded()) {
			addMarkers();
		} else {
			map.on("load", addMarkers);
		}

		const handleZoom = () => {
			const zoom = map.getZoom();
			for (const dot of dotMarkersRef.current) {
				applyDotScale(dot.element, zoom, dot.latitude, dot.color);
			}
		};

		map.on("zoom", handleZoom);

		return () => {
			map.off("zoom", handleZoom);
		};
	}, [pings, onPingClick]);

	return (
		<div ref={containerRef} className={styles.container} style={{ height }} />
	);
}
